import { AxiosResponse } from 'axios';
import { apiService } from './ApiService';

// Auto-captured (requested term → matched catalog part) example. The flywheel
// records these after each search; an admin approves the good ones, and only
// approved rows are compiled back into the OE search prompt.
export interface KnowledgeTermExample {
    id: number;
    catalog: string;
    language: string;
    organization_id?: number | null;  // null = global (all orgs)
    term: string;
    part_name: string;
    part_id?: string | null;
    hit_count: number;
    avg_score?: number | null;
    approved: boolean;
    approved_by?: string | null;
    approved_at?: string | null;
    last_seen: string;
    created_at: string;
}

export interface KnowledgeTermExampleListResponse {
    status: string;
    examples: KnowledgeTermExample[];
    total_count: number;
}

export interface PruneResult {
    status: string;
    deleted: number;
    retention_days: number;
}

const BASE = '/api/admin/knowledge-examples';

export class KnowledgeExamplesService {
    private static instance: KnowledgeExamplesService;

    public static getInstance(): KnowledgeExamplesService {
        if (!KnowledgeExamplesService.instance) {
            KnowledgeExamplesService.instance = new KnowledgeExamplesService();
        }
        return KnowledgeExamplesService.instance;
    }

    public async list(params?: {
        catalog?: string;
        language?: string;
        term?: string;
        organization_id?: number;
        global_only?: boolean;
        approved?: boolean;
        min_hits?: number;
        limit?: number;
        offset?: number;
    }): Promise<AxiosResponse<KnowledgeTermExampleListResponse>> {
        return apiService.get(BASE, { params });
    }

    public async update(
        id: number,
        data: Partial<Pick<KnowledgeTermExample, 'term' | 'part_name' | 'part_id' | 'catalog' | 'language'>>,
    ): Promise<AxiosResponse<KnowledgeTermExample>> {
        return apiService.put(`${BASE}/${id}`, data);
    }

    public async approve(id: number): Promise<AxiosResponse<KnowledgeTermExample>> {
        return apiService.post(`${BASE}/${id}/approve`);
    }

    public async unapprove(id: number): Promise<AxiosResponse<KnowledgeTermExample>> {
        return apiService.post(`${BASE}/${id}/unapprove`);
    }

    public async remove(id: number): Promise<AxiosResponse<any>> {
        return apiService.delete(`${BASE}/${id}`);
    }

    /** Delete several example aggregates at once. Returns the deleted count. */
    public async bulkRemove(ids: number[]): Promise<AxiosResponse<{ status: string; deleted: number }>> {
        return apiService.post(`${BASE}/bulk-delete`, { ids });
    }

    /** Prune the raw observation log (older than `days`, default 90). Aggregates untouched. */
    public async pruneObservations(days?: number): Promise<AxiosResponse<PruneResult>> {
        return apiService.post(`${BASE}/observations/prune`, undefined, {
            params: days != null ? { days } : undefined,
        });
    }
}

export const knowledgeExamplesService = KnowledgeExamplesService.getInstance();
